/**
 * "Logos & colours" tab of the Brand kit: the brand colour codes and
 * the LampTwist logo library, mirrored from the Google Drive folder and
 * served as static files from /brand-assets/logo/. Read-only — files
 * are added by putting them in public/brand-assets/logo and
 * regenerating ./files.ts.
 *
 * Colour values are the Canva brand palette, codes confirmed by Doreen
 * (Sep 24, 2026).
 */
import { useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { useToast } from '../../components/ui/Toast';
import { LOGO_GROUPS, LogoFile } from './files';

const README_URL = '/brand-assets/logo/README-Logo-Usage-Guide.txt';

interface BrandColor {
  name: string;
  hex: string;
  rgb: string;
  cmyk: string;
  use: string;
  border?: boolean;
}

const BRAND_COLORS: BrandColor[] = [
  { name: 'Yellow', hex: '#FFBD09', rgb: '255 · 189 · 9', cmyk: '0 · 26 · 96 · 0', use: 'Accent. The lamp icon, highlights, calls to action.' },
  { name: 'Dark green', hex: '#2B4138', rgb: '43 · 65 · 56', cmyk: '34 · 0 · 14 · 75', use: 'Primary. Wordmark, headings, dark backgrounds, text.' },
  { name: 'Off-white', hex: '#FBFBFA', rgb: '251 · 251 · 250', cmyk: '0 · 0 · 0 · 2', use: 'Page and slide backgrounds.', border: true },
  { name: 'Sand', hex: '#EDECE5', rgb: '237 · 236 · 229', cmyk: '0 · 0 · 3 · 7', use: 'Secondary surfaces, cards, soft blocks.', border: true },
  { name: 'Green', hex: '#469E7A', rgb: '70 · 158 · 122', cmyk: '56 · 0 · 23 · 38', use: 'Secondary accent. Titles in presentations, links, tags.' },
];

const VERSION_INFO: Record<string, { title: string; hint: string }> = {
  FullLogo: { title: 'Full logo', hint: 'Icon + wordmark. The standard choice.' },
  Icon: { title: 'Icon', hint: 'Symbol only. Avatars, favicons, minimal use.' },
  Icon_Outline: { title: 'Icon outline', hint: 'Outline style. Graphic use.' },
};
const VERSION_ORDER = ['FullLogo', 'Icon', 'Icon_Outline'];
const COLOR_ORDER = ['FullColor', 'Green', 'Yellow', 'Black', 'White'];
const COLOR_LABEL: Record<string, string> = { FullColor: 'Full colour', Green: 'Green', Yellow: 'Yellow', Black: 'Black', White: 'White' };
const FORMAT_HINT: Record<string, string> = {
  EPS: 'Vector for printers and designers',
  PDF: 'Universal vector, opens anywhere',
  PNG: 'Transparent background, sharp for web & slides',
  JPG: 'Lightweight, no transparency, for email',
  SVG: 'Vector for web/apps, always sharp',
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const filesFor = (folder: string, sub: string, color: string): LogoFile[] =>
  LOGO_GROUPS.find((g) => g.folder === folder && g.sub === sub)?.variants.find((v) => v.variant === color)?.files ?? [];

const FileButton = ({ file }: { file: LogoFile }) => (
  <a
    className="btn ghost bk-file"
    href={file.url}
    download={file.name}
    title={`${FORMAT_HINT[file.ext] ?? file.ext} · ${formatBytes(file.size)}`}
  >
    <span className="ext">{file.ext}</span>
    <span className="size">{formatBytes(file.size)}</span>
  </a>
);

const ColorCard = ({ c }: { c: BrandColor }) => {
  const toast = useToast();
  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.push(`Copied ${label} ${value}`);
    } catch {
      toast.push('Could not copy');
    }
  };
  return (
    <div className="card bk-color">
      <div className="chip" style={{ background: c.hex, borderBottom: c.border ? '1px solid var(--border)' : undefined }} />
      <div className="body">
        <div className="name">
          <span>{c.name}</span>
        </div>
        <div className="use">{c.use}</div>
        <div className="codes">
          <button type="button" onClick={() => copy('HEX', c.hex)} title="Copy"><span className="k">HEX</span>{c.hex}</button>
          <button type="button" onClick={() => copy('RGB', c.rgb.replace(/ · /g, ', '))} title="Copy"><span className="k">RGB</span>{c.rgb}</button>
          <button type="button" onClick={() => copy('CMYK', c.cmyk.replace(/ · /g, ', '))} title="Copy"><span className="k">CMYK</span>{c.cmyk}</button>
        </div>
      </div>
    </div>
  );
};

const VersionTable = ({ sub }: { sub: string }) => {
  const info = VERSION_INFO[sub] ?? { title: sub, hint: '' };
  const colors = COLOR_ORDER.filter((c) => filesFor('LD_Digital', sub, c).length + filesFor('HD_Print', sub, c).length > 0);
  return (
    <div className="section">
      <div className="bk-row">
        <div className="section-title">{info.title}</div>
        <p className="bk-hint">{info.hint}</p>
      </div>
      <div className="card bk-table-wrap">
        <table className="bk-logo-table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>Preview</th>
              <th>Colour</th>
              <th>Digital · screen</th>
              <th>Print · vector</th>
            </tr>
          </thead>
          <tbody>
            {colors.map((color) => {
              const digital = filesFor('LD_Digital', sub, color);
              const print = filesFor('HD_Print', sub, color);
              const preview = digital.find((f) => f.ext === 'PNG') ?? digital.find((f) => f.ext === 'SVG');
              return (
                <tr key={color}>
                  <td>
                    <div className={`prev ${color === 'White' ? 'dark' : ''}`}>
                      {preview && <img src={preview.url} alt={`${info.title} ${COLOR_LABEL[color] ?? color}`} loading="lazy" />}
                    </div>
                  </td>
                  <td className="cname">{COLOR_LABEL[color] ?? color}</td>
                  <td><div className="bk-files">{digital.map((f) => <FileButton key={f.url} file={f} />)}</div></td>
                  <td><div className="bk-files">{print.map((f) => <FileButton key={f.url} file={f} />)}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const LogoFiles = () => {
  const [guideOpen, setGuideOpen] = useState(false);
  return (
    <>
      <div className="section">
        <div className="bk-row">
          <div className="section-title">Brand colours</div>
          <p className="bk-hint">The five brand colours from the Canva palette. Click a value to copy it. CMYK values are conversions — confirm with your printer for critical print work.</p>
        </div>
        <div className="bk-colors">
          {BRAND_COLORS.map((c) => <ColorCard key={c.hex} c={c} />)}
        </div>
      </div>

      <div className="section">
        <div className="bk-row">
          <div className="section-title">Logos</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn ghost" onClick={() => setGuideOpen((v) => !v)}>
              <Icon name="helpCircle" size={13} /> {guideOpen ? 'Hide guide' : 'How to pick a file'}
            </button>
            <a className="btn ghost bk-file" href={README_URL} download="README-Logo-Usage-Guide.txt">
              <span className="ext">README</span>
            </a>
          </div>
        </div>
        {guideOpen && (
          <div className="card bk-guide" style={{ marginBottom: 6 }}>
            <ul>
              <li><b>Website, email, slides, documents</b> → Digital files: PNG (transparent, ~2000 px), JPG (~1200 px, no transparency), SVG (scales without limit).</li>
              <li><b>Printer or external designer</b> → Print files: EPS or PDF, both vector.</li>
              <li><b>White</b> versions are for dark or photo backgrounds only.</li>
            </ul>
            <ul>
              <li><b>Standard</b> use the full logo; <b>minimal</b> use the icon; <b>graphic</b> use the icon outline.</li>
              <li>Never edit or recolour a logo yourself — ask for a new export.</li>
              <li>Naming: <code>LampTwist_[Version]_[Colour]_[HD|LD].[ext]</code></li>
            </ul>
          </div>
        )}
      </div>

      {VERSION_ORDER.map((sub) => <VersionTable key={sub} sub={sub} />)}
    </>
  );
};

export default LogoFiles;
